import * as React from 'react'
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl'
import { useQuery, useMutation } from 'react-apollo'
import { useFormState } from 'react-use-form-state'
import isMobile from 'ismobilejs'
import querystring from 'querystring'
import moment from 'moment'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons'

import { Card, CardBody } from '../../components/card'
import BankidCard from '../../components/bankid-card'
import Button from '../../components/button'
import Error from '../../components/error'
import GenericStep from '../signup-v3/components/generic-step'
import WhatYouGet from '../signup-v3/components/what-you-get'
import moneyBackStamp from '../signup-v3/components/step-1/components/hero/money-back-stamp.png'
import Price from '../../components/price'
import Spinner from '../../components/spinner'
import Quotes from '../../sections/quotes'
import { ReactComponent as BankIdSvg } from '../../components/functional/basic-signup/form/bankid.svg'
import { pushGAEvent } from '../../lib/utils'
import { Toggle } from '../../components/form'

import styles from './accept-offer.module.scss'

import {
  DooerComGetSubscriptionOffer,
  DooerComGetSubscriptionOfferVariables,
} from './__generated__/DooerComGetSubscriptionOffer'
import {
  DooerComAcceptSubscriptionOffer,
  DooerComAcceptSubscriptionOfferVariables,
} from './__generated__/DooerComAcceptSubscriptionOffer'

import { subscriptionOfferQuery, acceptSubscriptionOfferMutation } from './queries'
import BankidSessionStatus from '../../components/functional/basic-signup/bankid-session'
import Seo from '../../components/seo'

interface IAcceptOfferProps {
  match: {
    params: {
      organizationId: string
      id: string
    }
  }
}

interface IFormValues {
  agreements: boolean
}

function AcceptOffer({
  match: {
    params: { organizationId, id },
  },
}: IAcceptOfferProps) {
  const params = querystring.parse(window.location.search.replace(/^\?/, ''))
  const [success, setSuccess] = React.useState<boolean>(false)
  const [bankidSessionId, setBankidSessionId] = React.useState<string | null>(
    (params && typeof params.sessionId === 'string' && params.sessionId) || null
  )
  const [autoStartToken, setAutoStartToken] = React.useState<string | null>(null)
  const [formState, input] = useFormState<IFormValues>(
    {
      agreements: false,
    } as IFormValues,
    {
      withIds: true,
    }
  )

  const { loading: offerLoading, data: offerData, error: offerError } = useQuery<
    DooerComGetSubscriptionOffer,
    DooerComGetSubscriptionOfferVariables
  >(subscriptionOfferQuery, {
    variables: { organizationId, id },
    skip: !organizationId || !id,
  })

  const [acceptOffer, { loading: acceptLoading, error: acceptError }] = useMutation<
    DooerComAcceptSubscriptionOffer,
    DooerComAcceptSubscriptionOfferVariables
  >(acceptSubscriptionOfferMutation, {
    variables: { organizationId, id },
  })

  if (offerLoading) return <Spinner className={styles.spinner} variant="brand" />

  const subscriptionOffer = offerData && offerData.organization && offerData.organization.subscriptionOffer
  if (offerError || !organizationId || !id || !subscriptionOffer) {
    return <Error center value={{ defaultMessage: 'Tyvärr, någonting gick fel!', id: 'accept-offer.error' }} />
  }

  const feeExVat = subscriptionOffer.feeExVat || 0

  const showThankYou = success || subscriptionOffer.isAccepted
  const isExpired = subscriptionOffer.validThru
    ? moment(subscriptionOffer.validThru).isBefore(moment().startOf('day'))
    : false

  return (
    <>
      <Seo
        description="Hejdå bokföring. Hallå Dooer. Bli kund hos oss idag."
        image="https://www.dooer.com/images/og/og_index.jpg"
        title="Bli kund | Dooer"
        url="https://www.dooer.com/signup"
      />

      <GenericStep
        content={
          <div className={styles.content}>
            {!showThankYou && bankidSessionId ? (
              <BankidCard
                className={styles.bankIDCard}
                status={
                  <BankidSessionStatus
                    autoStartToken={autoStartToken}
                    onComplete={() => {
                      pushGAEvent('order-completed-accounting')
                      setBankidSessionId(null)
                      setAutoStartToken(null)
                      setSuccess(true)
                    }}
                    onFailure={() => {
                      setTimeout(() => {
                        setBankidSessionId(null)
                        setAutoStartToken(null)
                      }, 2000)
                    }}
                    redirect={encodeURI(
                      `${document.location.protocol}//${document.location.host}${document.location.pathname}?sessionId=${bankidSessionId}`
                    )}
                    sessionId={bankidSessionId}
                  />
                }
              />
            ) : (
              <div className={styles.priceCardWrapper}>
                <Card className={styles.priceCard}>
                  {!showThankYou && (
                    <div className={styles.priceWrapper}>
                      <FormattedMessage
                        defaultMessage="Pris för {title}"
                        id="accept-offer.card-price-heading"
                        tagName="p"
                        values={{ title: subscriptionOffer.title }}
                      />
                      <Price amount={feeExVat / 100} className={styles.amount} prefix="currency" />
                    </div>
                  )}
                  <CardBody>
                    {showThankYou && (
                      <>
                        <FormattedHTMLMessage
                          defaultMessage="Ditt Dooer-konto är nu skapat!"
                          id="accept-offer.thank-you-card-heading"
                          tagName="h3"
                        />

                        <FormattedHTMLMessage
                          defaultMessage="<p>Inom kort kommer en av våra ekonomer höra av sig för att hjälpa dig komma igång, men nu kan du luta dig tillbaka och njuta över att ha gjort ett smart val för dig och ditt företag.</p><p>Om du vill kan du logga in och utforska tjänsten redan nu.</p>"
                          id="accept-offer.thank-you-card-lede"
                          tagName="span"
                        />
                        <div className={styles.buttonWrapper}>
                          <Button
                            bankId
                            className={styles.successButton}
                            onClick={() => {
                              document.location.href = 'https://go.dooer.com/'
                            }}
                            variant="success"
                          >
                            <FormattedMessage defaultMessage="Logga in" id="accept-offer.thank-you-card-button" />
                          </Button>
                        </div>
                      </>
                    )}
                    {!showThankYou && (
                      <>
                        <FormattedHTMLMessage
                          defaultMessage="Slutför din beställning"
                          id="accept-offer.card-heading"
                          tagName="h3"
                        />

                        <FormattedHTMLMessage
                          defaultMessage="Detta pris baseras på uppgifter du lämnat till Dooer gällande bland annat antal anställda, antal transaktioner och årlig omsättning. Innan du slutför din beställning kan du läsa mer om hur vi arbetar och vad som krävs av dig och av Dooer i vårt&nbsp;<a href='/Uppdragsbrev_Dooer.pdf' target='_blank' rel='noopener noreferrer'>uppdragsbrev</a>."
                          id="accept-offer.card-lede"
                          tagName="p"
                        />

                        {subscriptionOffer.description && subscriptionOffer.description.html && (
                          <div className={styles.offerDescription}>
                            <i>
                              <FontAwesomeIcon icon={faExclamationTriangle} />
                            </i>
                            <div
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: subscriptionOffer.description.html,
                              }}
                            />
                          </div>
                        )}

                        {acceptError && (
                          <Error
                            center
                            value={{ defaultMessage: 'Tyvärr, någonting gick fel!', id: 'accept-offer.error' }}
                          />
                        )}

                        {isExpired && (
                          <Error
                            center
                            html
                            value={{
                              defaultMessage:
                                'Din offert är tyvärr inte längre giltig.<br/>Kontakta oss gärna för att få en ny.',
                              id: 'accept-offer.expired',
                            }}
                          />
                        )}

                        {!isExpired && (
                          <>
                            <Toggle
                              center
                              checked={formState.values.agreements}
                              input={input}
                              label={
                                <FormattedHTMLMessage
                                  defaultMessage="Jag har läst och godkänner Dooers <a href='https://dooer.com/tos' target='_blank' rel='noopener noreferrer'>allmänna villkor</a> och <a href='/Uppdragsbrev_Dooer.pdf' target='_blank' rel='noopener noreferrer'>uppdragsbrev</a>"
                                  id="signup-v3.step-8.agreements"
                                />
                              }
                              name="agreements"
                              required
                            />

                            <div className={styles.buttonWrapper}>
                              <Button
                                bankId
                                className={styles.successButton}
                                disabled={!formState.values.agreements}
                                loading={offerLoading || acceptLoading}
                                onClick={() =>
                                  acceptOffer()
                                    .then((res) => {
                                      if (
                                        res.data &&
                                        res.data.organization.subscriptionOffer &&
                                        res.data.organization.subscriptionOffer.acceptWithBankId
                                      ) {
                                        const session = res.data.organization.subscriptionOffer.acceptWithBankId
                                        setBankidSessionId(session.id)
                                        setAutoStartToken(session.autoStartToken)
                                        if (isMobile().phone || isMobile().tablet) {
                                          const redirect = encodeURI(
                                            `${document.location.protocol}//${document.location.host}${document.location.pathname}?sessionId=${session.id}`
                                          )
                                          window.open(
                                            `bankid:///?autostarttoken=${session.autoStartToken}&redirect=${redirect}`,
                                            '_blank'
                                          )
                                        }
                                      }
                                    })
                                    .catch((e) => {
                                      // eslint-disable-next-line no-console
                                      console.error(e)
                                    })
                                }
                                variant="success"
                              >
                                <FormattedMessage defaultMessage="Beställ bokföring" id="accept-offer.card-button" />
                              </Button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </CardBody>
                  <div className={styles.actionInfoWrapper}>
                    {showThankYou && (
                      <FormattedMessage
                        defaultMessage="Väl inloggad hittar du en mängd information och guider i vår akademi. Vi rekommenderar särskilt kapitlet Så fungerar Dooer som beskriver hur du arbetar med tjänsten."
                        id="accept-offer.thank-you-card-action-info.lede"
                        tagName="h4"
                      />
                    )}
                    {!showThankYou && (
                      <FormattedMessage
                        defaultMessage="Väljer du ”beställ bokföring” kommer du att få godkänna beställningen med BankID, sedan kommer vi kontakta dig och hjälpa dig igång. Om du inte vill beställa just nu, så kan du använda samma länk fram till {validThru} för att fortsatt ha tillgång till samma offert."
                        id="accept-offer.card-action-info.lede"
                        tagName="h4"
                        values={{ validThru: subscriptionOffer.validThru }}
                      />
                    )}
                  </div>
                </Card>
                <div className={styles.reassurance}>
                  <img alt="Dooer 90 day money back guarantee stamp" src={moneyBackStamp} />
                  <div className={styles.reassuranceText}>
                    <FormattedMessage
                      defaultMessage="Ingen uppsägningstid"
                      id="accept-offer.reassurance.no-notice"
                      tagName="strong"
                    />
                    <FormattedMessage
                      defaultMessage="Pengarna tillbaka om du inte är nöjd"
                      id="accept-offer.reassurance.money-back"
                      tagName="strong"
                    />
                    <FormattedMessage
                      defaultMessage="* Nöjdhetsgarantin gäller hela månadskostnaden av tjänsten men inte eventuella övriga tjänster."
                      id="accept-offer.reassurance.asterix"
                      tagName="p"
                    />
                  </div>

                  <div className={styles.bankidContainer}>
                    <BankIdSvg />
                    <FormattedMessage
                      defaultMessage="Vi använder oss av mobil BankID-signering för att vara säkra på att ingen obehörig försöker företräda ditt bolag. Smart, smidigt och säkert."
                      id="accept-offer.reassurance.bankid.description"
                      tagName="p"
                    />
                  </div>
                </div>
              </div>
            )}
            <Quotes variant="extraSmall" />
            <WhatYouGet />
          </div>
        }
        header={
          showThankYou ? (
            <FormattedMessage
              defaultMessage="Tack för din beställning!"
              id="accept-offer.success-header"
              tagName="h1"
            />
          ) : (
            <FormattedMessage defaultMessage="Här är ditt pris från Dooer" id="accept-offer.title" tagName="h1" />
          )
        }
      />
    </>
  )
}

export default AcceptOffer
